import { type SemanticMetricTilePayload, type SemanticTileLever } from '../../types';
import { type MetricGrade, type MetricTileData } from '../MetricTile';
import { type OptimizationMetricContext } from '../OptimizationModal';

/**
 * Maps the generated semantic-layer wire contract (SemanticMetricTilePayload)
 * onto the presentational MetricTile props. Pure + unit-testable; kept out of
 * the component file so react-refresh stays happy.
 */

const GRADE_BY_MEASURE: Record<string, MetricGrade> = {
  ratio: 'deterministic',
  count: 'count',
  count_distinct: 'distinct',
};

const LEVER_VERB: Record<string, string> = {
  template_change: 'Template change',
  rule_change: 'Rule change',
  schedule_change: 'Schedule change',
  workflow_change: 'Workflow change',
};

/**
 * The tile "?" copy: what the metric shows (`help_text`) + how it's measured
 * (`help_tracked_by`). Both backend-supplied; absent → no "?" renders.
 */
const helpFromWire = (d: SemanticMetricTilePayload): MetricTileData['help'] => {
  if (!d.help_text) return undefined;
  return { body: d.help_text, trackedBy: d.help_tracked_by ?? undefined };
};

type CleanPoint = { x: string; y: number };

const cleanTrend = (d: SemanticMetricTilePayload): CleanPoint[] =>
  (d.trend ?? []).filter((p): p is CleanPoint => p.x != null && p.y != null);

const formatValue = (d: SemanticMetricTilePayload): string => {
  if (d.value_pct != null) return `${Math.round(d.value_pct)}%`;
  if (d.value_count != null) return String(d.value_count);
  return '—';
};

const leverCopy = (l?: SemanticTileLever | null): string | undefined => {
  if (!l) return undefined;
  if (l.label) return l.label;
  if (!l.kind && !l.target) return undefined;
  const verb = (l.kind && LEVER_VERB[l.kind]) || l.kind || '';
  return l.target ? `${verb} → ${l.target}` : verb || undefined;
};

/** Δ since the lever — server value if present, else derived from the trend. */
const deltaRaw = (d: SemanticMetricTilePayload): number | undefined => {
  if (d.delta_since_lever != null) return d.delta_since_lever;
  const pts = cleanTrend(d);
  if (pts.length < 2) return undefined;
  const last = pts[pts.length - 1].y;
  const markerAt = d.markers?.[0]?.occurred_at;
  let base = pts[0].y;
  if (markerAt) {
    const t = new Date(markerAt).getTime();
    const before = pts.filter(p => new Date(p.x).getTime() <= t);
    // Marker predates the whole trend → no pre-lever baseline to measure from;
    // don't fabricate a delta off the trend start (it would overstate the gain).
    if (!before.length) return undefined;
    base = before[before.length - 1].y;
  }
  return Math.round(last - base);
};

/** Color reflects good/bad (per grader direction); glyph reflects actual direction. */
const buildDelta = (d: SemanticMetricTilePayload): MetricTileData['delta'] => {
  const raw = deltaRaw(d);
  if (raw == null) return undefined;
  if (raw === 0) return { dir: 'flat', label: '—' };
  const glyph = raw > 0 ? '▲' : '▼';
  const isGood = d.grader === 'descending' ? raw < 0 : raw > 0;
  return { dir: isGood ? 'up' : 'down', label: `${glyph} ${Math.abs(raw)}` };
};

/** The lever's concrete "what we'd change" copy — the target, or its label. */
const leverFix = (l?: SemanticTileLever | null): string | undefined =>
  l?.target ?? l?.label ?? undefined;

/** Pretty "Template change" chip from the lever kind. */
const leverKindLabel = (l?: SemanticTileLever | null): string | undefined => {
  if (!l?.kind) return undefined;
  return LEVER_VERB[l.kind] ?? l.kind;
};

/**
 * The optimization-modal context for a tile, or null when it's not an
 * opportunity. Drives BOTH whether the "Improve this" CTA shows (non-null) and
 * what the modal says — every field is backend-derived (the `opportunity`
 * caption is the diagnostic line), so the FE invents no thresholds.
 */
export const optimizationContextFromWire = (
  d: SemanticMetricTilePayload,
): OptimizationMetricContext | null => {
  const opp = d.opportunity;
  const fix = leverFix(d.lever);
  // No opportunity, or nothing to actually offer → no funnel.
  if (!opp || !fix) return null;
  const n = d.n ?? 0;
  return {
    name: d.display_name ?? d.slug,
    appliesTo: [d.applies_to_label, n ? `n=${n}` : null].filter(Boolean).join(' · '),
    standing: formatValue(d),
    standingCaption: opp.caption ?? '',
    fix,
    fixKind: leverKindLabel(d.lever),
    analytics: {
      slug: d.slug,
      valuePct: d.value_pct ?? null,
      targetPct: opp.target ?? null,
      gap: opp.gap ?? null,
      isOpportunity: true,
    },
  };
};

export const tileFromWire = (d: SemanticMetricTilePayload): MetricTileData => {
  const gated = d.meets_gate === false;
  const measure = d.measure ?? '';
  const n = d.n ?? 0;
  const minDen = d.min_denominator ?? 0;

  let grade: MetricGrade = GRADE_BY_MEASURE[measure] ?? 'deterministic';
  if (d.type === 'state') grade = 'state';

  return {
    name: d.display_name ?? d.slug,
    appliesTo: [d.applies_to_label, measure].filter(Boolean).join(' · '),
    grade,
    value: gated ? '—' : formatValue(d),
    valueCaption: gated ? undefined : (d.unit_label ?? undefined),
    nLabel: `n=${n}`,
    delta: gated ? undefined : buildDelta(d),
    lever: leverCopy(d.lever),
    spark: gated
      ? undefined
      : {
          points: cleanTrend(d).map(p => ({ x: p.x, y: p.y })),
          markerXs: (d.markers ?? [])
            .map(m => m.occurred_at)
            .filter((x): x is string => !!x),
        },
    gated: gated ? { applicable: n, threshold: minDen } : undefined,
    help: helpFromWire(d),
  };
};
